/**
 * Tenant API Service
 *
 * Handles all tenant-related API calls:
 * - Get tenants
 * - Add/update/delete tenants
 * - Vacating tenants
 * - Tenant arrears
 * - Tenant files
 * - Tenant transactions
 */

import {post, uploadFile} from './client';

export const tenantsApi = {
    /**
     * Get all tenants for a landlord
     * @param {string} landlordId - Landlord ID
     * @returns {Promise<Object>} List of tenants
     */
    getAll: async (landlordId) => {
        return post('pegasus/visionary/tenant/get/all/tenants', {
            landlord_id: landlordId,
        });
    },

    /**
     * Get tenants for a specific property
     * @param {Object} propertyData - Property details
     * @returns {Promise<Object>} List of property tenants
     */
    getByProperty: async (propertyData) => {
        return post('pegasus/visionary/tenant/get/property/tenants', propertyData);
    },

    /**
     * Add a new tenant
     * @param {Object} tenantData - Complete tenant information
     * @returns {Promise<Object>} Add tenant response
     */
    add: async (tenantData) => {
        return post('pegasus/visionary/tenant/add/tenant', tenantData);
    },

    /**
     * Delete a tenant
     * @param {Object} deleteData
     * @param {string} deleteData.tenant_id - Tenant ID
     * @param {string} deleteData.property_id - Property ID
     * @param {string} deleteData.property_name - Property name
     * @param {string} deleteData.unit - Unit number
     * @param {string} deleteData.landlord_email_address - Landlord email
     * @returns {Promise<Object>} Delete response
     */
    delete: async (deleteData) => {
        return post('pegasus/visionary/tenant/delete', deleteData);
    },

    /**
     * Get all vacating tenants for a landlord
     * @param {string} landlordId - Landlord ID
     * @returns {Promise<Object>} List of vacating tenants
     */
    getVacating: async (landlordId) => {
        return post('pegasus/visionary/tenant/vacating/tenants', {
            landlord_id: landlordId,
        });
    },

    /**
     * Restore a vacating tenant
     * @param {Object} restoreData
     * @param {string} restoreData.property_id - Property ID
     * @param {string} restoreData.tenant_id - Tenant ID
     * @param {string} restoreData.house_number - House number
     * @returns {Promise<Object>} Restore response
     */
    restore: async (restoreData) => {
        return post('pegasus/visionary/tenant/restore', restoreData);
    },

    /**
     * Update vacating tenant deposit details
     * @param {Object} depositData
     * @param {string} depositData.tenant_id - Tenant ID
     * @param {number} depositData.deposit_to_refund - Amount to refund
     * @returns {Promise<Object>} Update response
     */
    updateVacatingDeposit: async (depositData) => {
        return post('pegasus/visionary/tenant/vacating/tenants/deposit', depositData);
    },

    /**
     * Handle tenant deposit (for vacating tenants)
     * @param {Object} depositData - Deposit handling details
     * @returns {Promise<Object>} Handle deposit response
     */
    handleDeposit: async (depositData) => {
        return post('pegasus/visionary/tenant/handle/deposit', depositData);
    },

    /**
     * Record deposit as income
     * @param {Object} incomeData - Deposit income details
     * @returns {Promise<Object>} Income recording response
     */
    depositAsIncome: async (incomeData) => {
        return post('pegasus/visionary/tenant/deposit/income', incomeData);
    },

    /**
     * Get tenant arrears
     * @param {Object} arrearsData - Arrears query data
     * @returns {Promise<Object>} Arrears details
     */
    getArrears: async (arrearsData) => {
        return post('pegasus/visionary/tenant/get/tenant/arrears', arrearsData);
    },

    /**
     * Delete arrears record
     * @param {Object} deleteData - Arrears deletion data
     * @returns {Promise<Object>} Delete response
     */
    deleteArrears: async (deleteData) => {
        return post('pegasus/visionary/tenant/delete/arrears', deleteData);
    },

    /**
     * Mark arrears as dirty/flagged
     * @param {Object} arrearsData - Arrears to flag
     * @returns {Promise<Object>} Flag response
     */
    flagArrears: async (arrearsData) => {
        return post('pegasus/visionary/tenant/arrear/dirty', arrearsData);
    },

    /**
     * Update tenant files
     * @param {Object} filesData - Files update data
     * @returns {Promise<Object>} Update response
     */
    updateFiles: async (filesData) => {
        return post('pegasus/visionary/tenant/update/tenant/files', filesData);
    },

    /**
     * Update tenant deposit details
     * @param {Object} depositData
     * @param {number} depositData.tenant_id - Tenant ID
     * @param {Array} depositData.deposits - Array of deposit objects
     * @returns {Promise<Object>} Update response with total deposit
     */
    updateDepositDetails: async (depositData) => {
        return post('pegasus/visionary/tenant/deposit/update', depositData);
    },
};

export default tenantsApi;
